import type { MenuItemProps } from '@wix/editor-elements-definitions';
import type { StyleProperties } from '@wix/thunderbolt-becky-types';
import type { SkinDefinition } from '@wix/editor-elements-panel/dist/adapters/types';
import { dropdownSkinParams } from '../../skinParams/dropdownSkinParams';

type DropdownItem = MenuItemProps & {
  id: string;
  items: Array<MenuItemProps>;
};

const getDropdownComputedStyle = (
  dropdown: DropdownItem,
  slots?: Record<string, JSX.Element>,
): CSSStyleDeclaration => {
  const { slot } = dropdown;
  const slotElement = slot && slots?.[slot];
  if (!slotElement) {
    return {} as CSSStyleDeclaration;
  }
  const dropdownCompId = slotElement.props.id as string;
  const dropdownDomElement = document.getElementById(dropdownCompId);

  return getComputedStyle(dropdownDomElement!);
};

const getComputedStyleProperties = (
  dropdown: DropdownItem,
  skinParams: SkinDefinition,
  slots?: Record<string, JSX.Element>,
): StyleProperties => {
  const computedStyle = getDropdownComputedStyle(dropdown, slots);
  return Object.keys(skinParams).reduce(
    (aggregator, propertyName) => ({
      ...aggregator,
      [propertyName]: computedStyle?.getPropertyValue(`--${propertyName}`),
    }),
    {},
  );
};

const checkHasItems = (
  maybeDropdown: MenuItemProps,
  multiple?: boolean,
): maybeDropdown is DropdownItem => {
  const dropdownItems = maybeDropdown.items ?? [];
  const amount = multiple ? 1 : 0;
  return dropdownItems.length > amount;
};

const checkHasSubItems = (dropdown: DropdownItem, multiple?: boolean) =>
  dropdown.items?.some(dropdownItem => {
    const amount = multiple ? 1 : 0;
    const dropdownSubItems = dropdownItem.items ?? [];
    return dropdownSubItems.length > amount;
  });

const checkIsAttached = (
  dropdown: DropdownItem,
  slots?: Record<string, JSX.Element>,
) => {
  const computedStyle = getComputedStyleProperties(
    dropdown,
    dropdownSkinParams,
    slots,
  );
  return Object.values(computedStyle).every(style => {
    return style === 'initial' || style === undefined || style === '';
  });
};

const PreviewParamWeights = {
  isAttached: 100,
  hasItems: 50,
  hasSubItems: 25,
  hasMultipleItems: 10,
  hasMultipleSubItems: 5,
  isPhysical: 1,
};

type PreviewParamWeightKeys = keyof typeof PreviewParamWeights;
type DropdownPreviewChecklist = { [key in PreviewParamWeightKeys]: boolean } & {
  id: string;
};

export const getDropdownToPreview = ({
  items = [],
  slots,
}: {
  items?: Array<MenuItemProps>;
  slots?: Record<string, JSX.Element>;
}) => {
  const filteredItems = items.filter(item => checkHasItems(item) || item.slot);
  const dropdownsPreviewChecklist: Array<DropdownPreviewChecklist> =
    filteredItems.map(dropdown => {
      const isPhysical = Boolean(dropdown.slot);
      return {
        id: dropdown.id!,
        isPhysical,
        hasItems: checkHasItems(dropdown),
        hasMultipleItems: checkHasItems(dropdown, true),
        hasSubItems: checkHasSubItems(dropdown as DropdownItem),
        hasMultipleSubItems: checkHasSubItems(dropdown as DropdownItem, true),
        isAttached: isPhysical
          ? checkIsAttached(dropdown as DropdownItem, slots)
          : true,
      };
    });

  const dropdownsPreviewScore = dropdownsPreviewChecklist.reduce(
    (acc, { id, ...nextChecklist }) => {
      return {
        ...acc,
        [id]: Object.entries(nextChecklist).reduce(
          (totalScore, [key, value]) => {
            const weightKey = key as PreviewParamWeightKeys;
            return value
              ? PreviewParamWeights[weightKey] + totalScore
              : totalScore;
          },
          0,
        ),
      };
    },
    {} as Record<string, number>,
  );

  const [dropdownToShow] = filteredItems.sort((dropdownA, dropdownB) => {
    const dropdownAScore = dropdownsPreviewScore[dropdownA.id!];
    const dropdownBScore = dropdownsPreviewScore[dropdownB.id!];
    return dropdownBScore - dropdownAScore;
  });

  return dropdownToShow;
};
